import React, { useEffect, useMemo, useState } from 'react';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { KakarikoSelect } from '../../Inputs';
import { GridContext, KakarikoProgress } from '../..';
import { KakarikoGridContextType } from '../types';
import { StyledKakarikoPagination } from './styles';
import { IKakarikoPaginationProps } from './types';

export const defaultPageSize = 50;

export const pageSizeOptions = [5, 10, 25, 50, 100] as const;

export const KakarikoPagination = (props: IKakarikoPaginationProps) => {
  const {
    currentPage,
    data,
    hasLastPageSelector,
    pageSizeSelectDisabled,
    pageSize: propsPageSize = defaultPageSize,
    resetPage,
    totalRecords: propsTotalRecords,
  } = props;

  const {
    isFirstPage: cxtIsFirstPage,
    isLastPage: cxtIsLastPage,
    isLoading: cxtIsLoading,
    handleClickFirstPage,
    handleClickLastPage,
    handleClickNextPage,
    handleClickPrevPage,
    setIsLoading: cxtSetIsLoading,
    setPageSize: cxtSetPageSize,
    setRecords,
  } = React.useContext<KakarikoGridContextType>(GridContext);

  const [isFirstPage, setIsFirstPage] = useState<boolean>(
    cxtIsFirstPage ?? true,
  );
  const [isLastPage, setIsLastPage] = useState<boolean>(cxtIsLastPage ?? true);
  const [isLoading, setIsLoading] = useState<boolean>(cxtIsLoading ?? false);
  const [page, setPage] = useState<number>(currentPage ?? 0);
  const [pageDisplay, setPageDisplay] = useState(page);
  const [pageSize, setPageSize] = useState<number>(propsPageSize);
  const [pageFirstRecord, setPageFirstRecord] = useState<number | undefined>();
  const totalRecords = propsTotalRecords ?? data.length;

  const externalPagination = useMemo(
    () =>
      [
        cxtIsFirstPage,
        cxtIsLastPage,
        handleClickFirstPage,
        handleClickNextPage,
        handleClickPrevPage,
      ].every((ext) => ext != null),
    [
      cxtIsFirstPage,
      cxtIsLastPage,
      handleClickFirstPage,
      handleClickNextPage,
      handleClickPrevPage,
    ],
  );

  useEffect(() => {
    if (cxtIsFirstPage != null) setIsFirstPage(cxtIsFirstPage);
    if (cxtIsLastPage != null) setIsLastPage(cxtIsLastPage);
  }, [cxtIsFirstPage, cxtIsLastPage]);

  useEffect(() => {
    if (cxtIsLoading != null) setIsLoading(cxtIsLoading);
  }, [cxtIsLoading]);

  useEffect(() => {
    setPage(0);
  }, [resetPage]);

  // Set the page values to use in the page navigation based on either external current page, or internal page value.
  useEffect(() => {
    setPageDisplay(currentPage ?? page);
  }, [currentPage, page]);

  // Update the displayed records when the page changes.
  useEffect(() => {
    if (!externalPagination) {
      const start = page * pageSize;
      const end = start + pageSize;
      setRecords?.([...data.slice(start, end)]);
      setIsFirstPage(page === 0);
      setIsLastPage(!((page + 1) * pageSize < totalRecords));
      setPageFirstRecord(page * pageSize + 1);
    }
  }, [data, externalPagination, page, pageSize, totalRecords, setRecords]);

  // Update the page when the number of records per page changes.
  useEffect(() => {
    const newPage = Math.floor((pageFirstRecord ?? 0) / pageSize);
    setPage(newPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const loadToggle = async (fn: any, ...args: any) => {
    if (cxtSetIsLoading != null) cxtSetIsLoading(true);
    else setIsLoading(true);

    if (args.length) await fn(args[0]);
    else await fn();

    if (cxtSetIsLoading != null) cxtSetIsLoading(false);
    else setIsLoading(false);
  };

  const handleFirstPage = async () => {
    await loadToggle(
      handleClickFirstPage ?? setPage,
      handleClickFirstPage ?? 0,
    );
  };

  const handleLastPage = async () => {
    await loadToggle(
      handleClickLastPage ?? setPage,
      handleClickLastPage ?? Math.floor((totalRecords - 1) / pageSize),
    );
  };

  const handleNextPage = async () => {
    await loadToggle(
      handleClickNextPage ?? setPage,
      handleClickNextPage ?? page + 1,
    );
  };

  const handlePrevPage = async () => {
    await loadToggle(
      handleClickPrevPage ?? setPage,
      handleClickPrevPage ?? page - 1,
    );
  };

  const lastRecordOnPage = useMemo(
    () => Math.min(pageDisplay * pageSize + pageSize, totalRecords),
    [pageDisplay, pageSize, totalRecords],
  );

  return (
    <StyledKakarikoPagination className="pagination-bar">
      <div className="pagination-page">
        {!isLoading && pageDisplay * pageSize + 1} -{' '}
        {!isLoading && lastRecordOnPage} of {totalRecords}
      </div>
      <section className="pagination-navigation">
        {!pageSizeSelectDisabled && (
          <KakarikoSelect
            ariaLabel={'page size selector'}
            className={'pagination-navigation__page-size'}
            handleChange={(e) => {
              setPageSize(e);
              cxtSetPageSize?.(e);
            }}
            options={[...pageSizeOptions]}
            value={pageSize}
          />
        )}
        <button
          aria-label="navigate to first page"
          className="pagination-navigation__first-page pagination-icon"
          onClick={handleFirstPage}
          disabled={isFirstPage || isLoading}
          type="button"
        >
          <FirstPageIcon
            color={isFirstPage || isLoading ? 'disabled' : 'inherit'}
          />
        </button>
        <button
          aria-label="navigate to previous page"
          className="pagination-navigation__prev-page pagination-icon"
          onClick={handlePrevPage}
          disabled={isFirstPage || isLoading}
          type="button"
        >
          <NavigateBeforeIcon
            color={isFirstPage || isLoading ? 'disabled' : 'inherit'}
          />
        </button>
        <button
          aria-label="navigate to next page"
          className="pagination-navigation__next-page pagination-icon"
          onClick={handleNextPage}
          disabled={isLastPage || isLoading}
          type="button"
        >
          <NavigateNextIcon
            color={isLastPage || isLoading ? 'disabled' : 'inherit'}
          />
        </button>
        {hasLastPageSelector !== false && (
          <button
            aria-label="navigate to last page"
            className="pagination-navigation__last-page pagination-icon"
            onClick={handleLastPage}
            disabled={isLastPage || isLoading}
            type="button"
          >
            <LastPageIcon
              color={isLastPage || isLoading ? 'disabled' : 'inherit'}
            />
          </button>
        )}
        {isLoading && <KakarikoProgress size={40} />}
      </section>
    </StyledKakarikoPagination>
  );
};
